import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router';
import { useAuth } from '../context/AuthContext';
import { Alert, Badge, Loader, Title, Body, Icon, Table, TableColumn } from '../components/kern';

interface DashboardStats {
  totalFeedbacks: number;
  pendingFeedbacks: number;
  resolvedFeedbacks: number;
  averageRating: number;
  projectCount: number;
  recentFeedbacks: Feedback[];
}

interface Feedback {
  id: number;
  message: string;
  rating: number;
  status: string;
  project_name: string;
  url: string;
  created_at: string;
}

export function DashboardPage() {
  const { token } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchDashboardStats = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/dashboard/stats', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Fehler beim Laden der Statistiken');
      }

      const data = await response.json();
      setStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<
      string,
      { variant: 'info' | 'warning' | 'success' | 'danger'; label: string }
    > = {
      new: { variant: 'info', label: 'Neu' },
      pending: { variant: 'warning', label: 'In Bearbeitung' },
      resolved: { variant: 'success', label: 'Erledigt' },
      rejected: { variant: 'danger', label: 'Abgelehnt' },
    };
    const { variant, label } = statusMap[status] || statusMap.new;
    return <Badge variant={variant}>{label}</Badge>;
  };

  const renderStars = (rating: number) => {
    return (
      <span className="rating-stars">
        {Array.from({ length: 5 }, (_, i) => (
          <span key={i} className={`star ${i < rating ? 'star--filled' : ''}`}>
            ★
          </span>
        ))}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <Loader />
        <Body>Lade Dashboard...</Body>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <Alert variant="danger" title="Fehler">
          {error}
        </Alert>
      </div>
    );
  }

  // Fallback-Daten für die Demo
  const displayStats: DashboardStats = stats || {
    totalFeedbacks: 42,
    pendingFeedbacks: 8,
    resolvedFeedbacks: 32,
    averageRating: 4.2,
    projectCount: 3,
    recentFeedbacks: [],
  };

  const feedbackColumns: TableColumn<Feedback>[] = [
    {
      key: 'message',
      header: 'Nachricht',
      render: (feedback) => (
        <Link to={`/feedbacks/${feedback.id}`} className="kern-link">
          {feedback.message.substring(0, 50)}
          {feedback.message.length > 50 ? '...' : ''}
        </Link>
      ),
    },
    {
      key: 'rating',
      header: 'Bewertung',
      render: (feedback) => renderStars(feedback.rating),
    },
    {
      key: 'status',
      header: 'Status',
      render: (feedback) => getStatusBadge(feedback.status),
    },
    {
      key: 'created_at',
      header: 'Datum',
      render: (feedback) => formatDate(feedback.created_at),
    },
  ];

  return (
    <div className="dashboard-page">
      <header className="page-header">
        <hgroup>
          <Title size="large" as="h1">
            Dashboard
          </Title>
          <Body muted>Übersicht über alle Feedback-Aktivitäten</Body>
        </hgroup>
      </header>

      <div className="kern-container">
        <div className="kern-row">
          <div className="kern-col-xl-3 kern-col-md-6 kern-col-sm-12">
            <article className="kern-card stat-card">
              <div className="kern-card__container">
                <div className="stat-card-content">
                  <div className="stat-icon stat-icon--primary">
                    <Icon name="mail" size="large" />
                  </div>
                  <div className="stat-content">
                    <span className="kern-heading-large">{displayStats.totalFeedbacks}</span>
                    <Body size="small" muted>
                      Gesamt Feedbacks
                    </Body>
                  </div>
                </div>
              </div>
            </article>
          </div>

          <div className="kern-col-xl-3 kern-col-md-6 kern-col-sm-12">
            <article className="kern-card stat-card">
              <div className="kern-card__container">
                <div className="stat-card-content">
                  <div className="stat-icon stat-icon--warning">
                    <Icon name="autorenew" size="large" />
                  </div>
                  <div className="stat-content">
                    <span className="kern-heading-large">{displayStats.pendingFeedbacks}</span>
                    <Body size="small" muted>
                      Offen
                    </Body>
                  </div>
                </div>
              </div>
            </article>
          </div>

          <div className="kern-col-xl-3 kern-col-md-6 kern-col-sm-12">
            <article className="kern-card stat-card">
              <div className="kern-card__container">
                <div className="stat-card-content">
                  <div className="stat-icon stat-icon--success">
                    <Icon name="check" size="large" />
                  </div>
                  <div className="stat-content">
                    <span className="kern-heading-large">{displayStats.resolvedFeedbacks}</span>
                    <Body size="small" muted>
                      Erledigt
                    </Body>
                  </div>
                </div>
              </div>
            </article>
          </div>

          <div className="kern-col-xl-3 kern-col-md-6 kern-col-sm-12">
            <article className="kern-card stat-card">
              <div className="kern-card__container">
                <div className="stat-card-content">
                  <div className="stat-icon stat-icon--info">
                    <Icon name="success" size="large" />
                  </div>
                  <div className="stat-content">
                    <span className="kern-heading-large">
                      {displayStats.averageRating.toFixed(1)}
                    </span>
                    <Body size="small" muted>
                      Ø Bewertung
                    </Body>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </div>
      </div>

      <div className="kern-container">
        <div className="kern-row">
          <div className="kern-col-xl-8 kern-col-md-12">
            <article className="kern-card dashboard-card">
              <div className="kern-card__container">
                <header className="kern-card__header">
                  <div className="card-header-row">
                    <Title as="h2">Neueste Feedbacks</Title>
                    <Link to="/feedbacks" className="kern-link">
                      <span className="kern-icon kern-icon--arrow-forward" aria-hidden="true" />
                      Alle anzeigen
                    </Link>
                  </div>
                </header>
                <section className="kern-card__body">
                  {displayStats.recentFeedbacks.length > 0 ? (
                    <Table
                      data={displayStats.recentFeedbacks.slice(0, 5)}
                      columns={feedbackColumns}
                      keyExtractor={(feedback) => feedback.id}
                      size="small"
                    />
                  ) : (
                    <div className="empty-state">
                      <Body>Noch keine Feedbacks vorhanden.</Body>
                      <Body size="small" muted>
                        Feedbacks werden hier angezeigt, sobald sie eingehen.
                      </Body>
                    </div>
                  )}
                </section>
              </div>
            </article>
          </div>

          <div className="kern-col-xl-4 kern-col-md-12">
            <article className="kern-card dashboard-card">
              <div className="kern-card__container">
                <header className="kern-card__header">
                  <Title as="h2">Schnellaktionen</Title>
                </header>
                <section className="kern-card__body">
                  <fieldset className="kern-fieldset">
                    <div className="kern-fieldset__body kern-fieldset__body--horizontal">
                      <Link to="/feedbacks" className="kern-btn kern-btn--secondary">
                        <Icon name="checklist" />
                        <span className="kern-label">Feedbacks verwalten</span>
                      </Link>
                      <Link to="/projects" className="kern-btn kern-btn--secondary">
                        <Icon name="home" />
                        <span className="kern-label">Projekte verwalten</span>
                      </Link>
                      <Link to="/settings" className="kern-btn kern-btn--secondary">
                        <Icon name="edit" />
                        <span className="kern-label">Einstellungen</span>
                      </Link>
                    </div>
                  </fieldset>
                </section>
              </div>
            </article>
          </div>
        </div>
      </div>
    </div>
  );
}
